import React from "react";

import SvgMessages from "@svg/messages";

import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { ListItem } from "@replo/design-system/components/list-item/ListItem";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { skipToken } from "@tanstack/react-query";
import {
  useActiveTriggerJobChats,
  useStreamingChats,
} from "@v2/features/chat/store/ChatStoreContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import {
  useLocalStorage,
  useLocalStorageState,
} from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { chatRouter } from "@v2/trpc/routes/chat";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { z } from "zod";

const PAGE_SIZE = 50;

function ManageChats() {
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const storage = useLocalStorage();
  const streamingChats = useStreamingChats();
  const activeTriggerJobs = useActiveTriggerJobChats();
  const [, setIsChatSidebarOpen] = useLocalStorageState(
    "replo.editor.chatSidebarOpen",
    false,
    { schema: z.boolean() },
  );

  const {
    data: chatsData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
  } = chatRouter.getPaginatedChats.useInfiniteQuery(
    projectId
      ? {
          projectId,
          limit: PAGE_SIZE,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
      staleTime: 0,
    },
  );

  const chats = React.useMemo(
    () => chatsData?.pages?.flatMap((page) => page.chats) ?? [],
    [chatsData],
  );

  useOnValueChange(chatsData, (oldValue, newValue) => {
    const stored = storage.getItem("replo.editor.chatSidebarOpen");
    const prevCount = oldValue?.pages?.[0]?.chats?.length ?? 0;
    const nextCount = newValue?.pages?.[0]?.chats?.length ?? 0;
    if (stored != null) {
      return;
    }

    if (prevCount === 0 && nextCount > 0) {
      setIsChatSidebarOpen(true);
    }
  });

  const handleNewChat = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("chatId", "");
      return newParams;
    });
    setIsChatSidebarOpen(false);
    logEvent("chat.new");
  };

  const handleChatClick = (chatId: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("chatId", chatId);
      return newParams;
    });
  };

  if (!projectId || !user?.id) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        Error Loading Chats
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-full min-h-0 w-full relative">
        <div className="px-3 pt-4 pb-4">
          <div className="mb-4">
            <Skeleton className="h-5 w-20" />
          </div>
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex-1 px-3 pb-3 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="w-full py-3 px-3">
              <Skeleton key={i} className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 md:p-3 flex-col h-full min-h-0 w-full md:bg-sheet relative">
      <h2 className="hidden md:block text-sm font-semibold text-default m-0 mb-4">
        All Chats
      </h2>
      {chats.length > 0 && (
        <span className="mb-1">
          <Button
            variant="secondary"
            size="base"
            className="w-full"
            onClick={handleNewChat}
          >
            New Chat
          </Button>
        </span>
      )}
      <div
        id="chats-scroll-container"
        className="flex-1 overflow-y-auto no-scrollbar"
      >
        {chats.length === 0 ? (
          <div className="h-full w-full flex items-center justify-center p-4">
            <Empty
              media={<SvgMessages className="h-16 w-auto" />}
              title="No chats yet"
              description="Chat history will show up here."
            >
              <Button variant="secondary" size="base" onClick={handleNewChat}>
                Create new chat
              </Button>
            </Empty>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={chats.length}
            next={() => void fetchNextPage()}
            hasMore={hasNextPage ?? false}
            scrollableTarget="chats-scroll-container"
            loader={
              <div className="w-full py-3 px-3">
                <Skeleton className="h-4 w-3/4" />
              </div>
            }
          >
            <div className="flex flex-col gap-2 no-scrollbar">
              {chats.map((chat) => {
                const isStreaming = streamingChats.includes(chat.id);
                const hasRunningPageGenerationJob = activeTriggerJobs.includes(
                  chat.id,
                );

                return (
                  <ListItem
                    key={chat.id}
                    render={<button type="button" />}
                    selected={chatId === chat.id}
                    className="w-full"
                    UNSAFE_className="hover:bg-surface"
                    onClick={() => handleChatClick(chat.id)}
                    label={
                      <div className="flex flex-row items-center gap-2">
                        {(isStreaming || hasRunningPageGenerationJob) && (
                          <div className="flex-shrink-0">
                            <Spinner className="size-3" />
                          </div>
                        )}
                        <div className="whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                          {chat.title}
                        </div>
                      </div>
                    }
                  />
                );
              })}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}

export default ManageChats;
